import React, { useState } from "react";
import axios from "axios";
import { ArrowRight } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import SEO from "@/components/SEO";
import { toast } from "sonner";
import { trackClick } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BENEFITS = [
  { t: "Premium Shelf Presence", d: "Packaging engineered for the modern Indian shelf." },
  { t: "Attractive Margins", d: "Healthy, sustainable margin structure for the long term." },
  { t: "Marketing Support", d: "POS material, festival merchandising and seasonal kits." },
  { t: "Display Support", d: "Designed shelf-talkers, posters and counter units." },
  { t: "Fast Replenishment", d: "A reliable supply chain that holds during festivals." },
  { t: "Retailer-First Pricing", d: "Transparent slabs. Strong incentives. No surprises." },
];

const Retailer = () => {
  const [form, setForm] = useState({ store: "", owner: "", location: "", phone: "", monthly: "" });
  const onSubmit = async (e) => {
    e.preventDefault();
    trackClick("retailer-enquiry-submit");
    try {
      await axios.post(`${API}/leads`, {
        kind: "retailer",
        company: form.store,
        name: form.owner,
        location: form.location,
        phone: form.phone,
        monthly_purchase: form.monthly,
      });
      toast.success("Thank you — our retail team will reach out within 48 hours.");
      setForm({ store: "", owner: "", location: "", phone: "", monthly: "" });
    } catch (_) {
      toast.error("Could not submit — please try again or email us.");
    }
  };

  return (
    <div data-testid="retailer-page">
      <SEO
        title="Become a Sanatan Dharma Retail Partner"
        description="Bring a trusted Indian household brand to your shelf. Premium margins, marketing support, fast replenishment. Apply to become a Sanatan Dharma retail partner."
        keywords="sanatan dharma retail partner, puja product distributor, become retailer, wholesale puja items india, sanatan dharma dealership"
        path="/retailer"
      />
      <section className="mandala-bg border-b border-[#3B2A1A]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32 grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7">
            <p className="overline mb-5">Retail Partnership</p>
            <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[0.98] text-[#3B2A1A] tracking-tight">
              Partner <span className="italic text-[#C89D3C] font-light">With Trust.</span>
            </h1>
          </div>
          <div className="lg:col-span-5 text-lg leading-relaxed text-[#5C4A3A] font-light">
            <p>Bring a trusted Indian household brand to your shelf — with premium margins, strong support and a category that moves all year.</p>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading overline="Benefits" title="A partnership built to last." />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#3B2A1A]/10 mt-14 border border-[#3B2A1A]/10">
            {BENEFITS.map((b, i) => (
              <div key={b.t} className="bg-white p-8 lg:p-10" data-testid={`retailer-benefit-${i}`}>
                <p className="font-serif-display text-3xl text-[#C89D3C]">0{i + 1}</p>
                <h3 className="font-serif-display text-2xl text-[#3B2A1A] mt-5">{b.t}</h3>
                <p className="text-sm text-[#5C4A3A] mt-3 font-light leading-relaxed">{b.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-24 bg-[#3B2A1A] text-[#FBF6EC]" data-testid="retailer-form-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <p className="overline text-[#E2C37B] mb-5">Become A Partner</p>
            <h2 className="font-serif-display text-4xl lg:text-5xl leading-[1.05] tracking-tight">A short form. A real reply.</h2>
            <p className="text-sm text-[#FBF6EC]/75 mt-5 font-light leading-relaxed">Our retail team responds personally within 48 hours.</p>
          </div>
          <form onSubmit={onSubmit} className="lg:col-span-7 space-y-5" data-testid="retailer-form">
            {[
              { k: "store", l: "Store Name" },
              { k: "owner", l: "Owner Name" },
              { k: "location", l: "Location (City, State)" },
              { k: "phone", l: "Phone" },
              { k: "monthly", l: "Estimated Monthly Purchase (₹)" },
            ].map((f) => (
              <div key={f.k}>
                <label className="text-[10px] uppercase tracking-[0.22em] text-[#E2C37B]">{f.l}</label>
                <input
                  required
                  data-testid={`retailer-input-${f.k}`}
                  value={form[f.k]}
                  onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                  className="w-full bg-transparent border-b border-[#FBF6EC]/30 py-3 text-base text-[#FBF6EC] focus:outline-none focus:border-[#C89D3C]"
                />
              </div>
            ))}
            <button type="submit" data-testid="retailer-submit-btn" className="mt-4 inline-flex items-center gap-2 bg-[#C89D3C] hover:bg-[#E2C37B] text-[#3B2A1A] uppercase tracking-[0.18em] text-xs font-semibold px-8 py-4 transition-colors">
              Send Enquiry <ArrowRight size={14} />
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Retailer;
